import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { each, isEmpty } from 'lodash';
import { getPrices, getRiskAmounts, setAge, setPaymentFrequency, setRange, setRiskAmount, setWithChildren } from '../../redux/modules/oncoCareCalculation';
import OncoCareCalculator from '../../components/OncoCareCalculator';
import CalculatorSummary from '../../components/OncoCareCalculator/CalculatorSummary';
import EfesContentHeader from '../../components/EfesContentHeader';
import useDeviceResize from '../../hooks/useDeviceResize';
import useSchedule from '../../hooks/useSchedule';
import { getContractSummary, getDiscount, getDiscountedPrice, getTotalprice } from '../../helpers/oncoCare/oncoCareCalcHelpers';
import { bindSummaryContractData } from '../../helpers/oncoCare/bindData';
import { EFES_CONTENT_WIDTH, EFES_SUMMARY_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import { ACTION_NAME } from '../../constants';
import useIsAutomaticRecurringPayment from '../../hooks/useIsAutomaticRecurringPayment';
import useOncoCareContractRange from '../../hooks/useOncoCareContractRange';
import summaryWrapperUseStyles from '../../components/MUI/SummaryWrapper/makeStyles';

import OncoCareCalcHeader from '../../img/oncoCareCalcHeader.svg';

const OncoCareCalculatorContainer = ({
  formProps,
  oncoCareDataSelector,
  actionName,
  buyButtonHandler,
}) => {
  const isDeviceLgSize = useDeviceResize('lg');
  const summaryWrapperClasses = summaryWrapperUseStyles();

  const [openMobileSummary, setOpenMobileSummary] = useState();
  const oncoCareDetails = useSelector(oncoCareDataSelector, shallowEqual);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    age,
    withChildren,
    riskAmount,
    riskAmounts,
    isFetchingRiskAmounts,
    isFetchingPrice,
    paymentFrequencies,
    prices,
    paymentFrequency,
    range,
  } = oncoCareDetails;

  const { setError } = formProps;
  const isAutomaticRecurringPayment = useIsAutomaticRecurringPayment(paymentFrequency);
  const oncoCareContractRange = useOncoCareContractRange();

  const calculatorBackendErrors = useSelector(
    (state) => state.oncoCareContract.calculatorBackendErrors,
    shallowEqual,
  );

  const dispatchAge = useCallback((newAge) => dispatch(setAge(newAge, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchWithChildren = useCallback(
    (withChild) => dispatch(setWithChildren(withChild, actionName)), [
      dispatch,
      actionName,
    ],
  );

  const dispatchRiskAmount = useCallback((amount) => dispatch(setRiskAmount(amount, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchPaymentFrequency = useCallback(
    (frequency) => dispatch(setPaymentFrequency(frequency, actionName)), [
      dispatch,
      actionName,
    ],
  );

  useEffect(() => {
    if (isEmpty(riskAmounts)) {
      dispatch(getRiskAmounts(actionName));
    }
  }, [dispatch, actionName, riskAmounts]);

  useEffect(() => {
    dispatch(
      getPrices(
        {
          age,
          withChildren,
          riskAmount,
        },
        actionName,
      ),
    );
  }, [withChildren, age, riskAmount, dispatch, actionName]);

  useEffect(() => {
    dispatch(setRange(oncoCareContractRange, actionName));
  }, [actionName, dispatch, oncoCareContractRange]);

  useEffect(() => {
    if (calculatorBackendErrors) {
      const errorList = [];
      each(calculatorBackendErrors, (e, key) => {
        errorList.push({
          type: 'backend',
          name: key,
          message: `backendErrors.${e}`,
        });
      });
      setError(errorList);
    }
  }, [calculatorBackendErrors, setError]);

  const totalPrice = useMemo(() => getTotalprice(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const totalPriceAfterDiscount = useMemo(() => getDiscountedPrice(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const totalDiscount = useMemo(() => getDiscount(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const contractDetails = useMemo(
    () => bindSummaryContractData(t, range, riskAmount[0]),
    [t, range, riskAmount],
  );

  const contractSummary = useMemo(
    () => getContractSummary(totalDiscount, totalPrice, t, paymentFrequency, isFetchingPrice),
    [totalDiscount, totalPrice, t, paymentFrequency, isFetchingPrice],
  );

  const calculatedSchedules = useSchedule(paymentFrequency.value, paymentFrequencies);

  return (
    <>
      <EfesContentHeader
        headerText="oncoCareContract.title"
        headerImage={OncoCareCalcHeader}
        imageWidth={50}
      />
      <Grid container>
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH,
            paddingRight: isDeviceLgSize ? 0 : 16,
          }}
        >
          <OncoCareCalculator
            formProps={formProps}
            setAge={dispatchAge}
            age={age}
            setWithChildren={dispatchWithChildren}
            withChildren={withChildren}
            riskAmount={riskAmount}
            setRiskAmount={dispatchRiskAmount}
            riskAmounts={riskAmounts}
            isFetchingRiskAmounts={isFetchingRiskAmounts}
            isFetchingPrice={isFetchingPrice}
            prices={prices}
            paymentFrequencies={paymentFrequencies}
            setPaymentFrequency={dispatchPaymentFrequency}
            paymentFrequency={paymentFrequency}
            handleOpenMobileSummary={() => setOpenMobileSummary(!openMobileSummary)}
            buyButtonHandler={buyButtonHandler}
            totalPriceAfterDiscount={
              (isAutomaticRecurringPayment && calculatedSchedules.length)
                ? calculatedSchedules[0].amount : totalPriceAfterDiscount
            }
          />
        </Grid>
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_SUMMARY_WIDTH,
            paddingLeft: isDeviceLgSize ? 0 : 16,
          }}
        >
          <div className={summaryWrapperClasses.summaryWrapperBox}>
            <CalculatorSummary
              onDismiss={() => setOpenMobileSummary(!openMobileSummary)}
              isFetchingPrice={isFetchingPrice}
              contractDetails={contractDetails}
              openMobileSummary={openMobileSummary}
              buyButtonHandler={buyButtonHandler}
              totalPriceAfterDiscount={
                  (isAutomaticRecurringPayment && calculatedSchedules.length)
                    ? calculatedSchedules[0].amount : totalPriceAfterDiscount
                }
              contractSummary={contractSummary}
              paymentFrequency={paymentFrequency}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
};

OncoCareCalculatorContainer.propTypes = {
  oncoCareDataSelector: PropTypes.func,
  formProps: PropTypes.shape({
    errors: PropTypes.shape({}),
    setValue: PropTypes.func,
    register: PropTypes.func,
    setError: PropTypes.func,
  }).isRequired,
  actionName: PropTypes.string,
  buyButtonHandler: PropTypes.func,
};

OncoCareCalculatorContainer.defaultProps = {
  oncoCareDataSelector: (state) => state.oncoCareCalculation,
  actionName: ACTION_NAME.CALCULATOR,
  buyButtonHandler: () => { },
};

export default OncoCareCalculatorContainer;
